import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader, Alert, Spinner, Button } from 'reactstrap';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const BarcodeScannerModal = ({
    isOpen,
    onClose,
    onScan,
    title = 'Escanear Código',
    scanType = 'both'
}) => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const scannerRef = useRef(null);
    const SCANNER_ID = "html5qr-code-full-region";

    // Formatos recomendados simplificados
    const formats = {
        qr: [Html5QrcodeSupportedFormats.QR_CODE],
        barcode: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.UPC_A
        ],
        both: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.EAN_13
        ]
    };

    const stopScanner = async () => {
        if (scannerRef.current && scannerRef.current.isScanning) {
            try {
                await scannerRef.current.stop();
                scannerRef.current.clear();
            } catch (err) {
                console.error("Error al detener:", err);
            }
        }
        scannerRef.current = null;
    };

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setError(null);

            // Timeout pequeño para esperar que el DOM del modal esté listo
            const timeoutId = setTimeout(async () => {
                try {
                    scannerRef.current = new Html5Qrcode(SCANNER_ID);
                    await scannerRef.current.start(
                        { facingMode: "environment" },
                        { fps: 10, qrbox: { width: 250, height: 250 } },
                        (decodedText) => {
                            // DETENER inmediatamente para evitar múltiples lecturas o bucles
                            stopScanner().then(() => {
                                onScan(decodedText);
                                onClose(); // Cerramos desde aquí para flujo limpio
                            });
                        },
                        () => { /* Ignorar fallos de lectura silenciosos */ }
                    );
                    setIsLoading(false);
                } catch (err) {
                    setError("No se pudo acceder a la cámara o el ID no existe.");
                    setIsLoading(false);
                }
            }, 300);

            return () => {
                clearTimeout(timeoutId);
                stopScanner();
            };
        }
    }, [isOpen]); // Solo depende de si se abre o cierra

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered backdrop="static">
            <ModalHeader toggle={onClose}>{title}</ModalHeader>
            <ModalBody className="position-relative" style={{ minHeight: '300px' }}>
                {error && <Alert color="danger">{error}</Alert>}

                {isLoading && !error && (
                    <div className="text-center p-5">
                        <Spinner color="primary" />
                        <p>Iniciando cámara...</p>
                    </div>
                )}

                {/* Contenedor Único y Fijo */}
                <div id={SCANNER_ID} style={{ width: '100%' }}></div>

                <div className="text-center mt-3">
                    <Button color="secondary" onClick={onClose} size="sm">Cancelar</Button>
                </div>
            </ModalBody>
        </Modal>
    );
};

BarcodeScannerModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onScan: PropTypes.func.isRequired,
    title: PropTypes.string,
    scanType: PropTypes.string
};

export default BarcodeScannerModal;